import {
    EVENT_ADOPTION_METRICS,
    EVENT_ADOPTION_RESULT_LAYOUT,
    buildVisitorAppAdoptionAggregations,
    parseVisitorAppAdoptionResults,
    buildVisitorAppEngagementAggregations,
    parseVisitorAppEngagementResults,
    buildVisitorAppRageClicksAggregations,
    parseVisitorAppRageClicksResults,
    buildEventAdoptionAllUsersIncrease,
    buildEventAdoptionAllUsersDecrease,
    buildEventAdoptionNewUsers,
    parseEventAdoptionResults,
    buildVisitorRetentionAggregations,
    buildAccountRetentionAggregations,
    parseRetentionTrendsResults,
} from '@pendo/middleware';
import { i18n_t as $t } from '@/utils/i18n';
import moment from '@/utils/moment-wrapper';
import momentTimeZone from 'moment-timezone';
import { addCommasToNumber } from '@/utils/formatters';
import { ANONYMOUS_ONLY_SEGMENT_ID, SIGNAL_TYPES } from './signals.constants.js';

function getEventAdoptionLayout (segmentId) {
    if (segmentId === ANONYMOUS_ONLY_SEGMENT_ID) {
        return EVENT_ADOPTION_RESULT_LAYOUT.filter(({ metric }) => metric !== EVENT_ADOPTION_METRICS.NEW_USERS);
    }

    return EVENT_ADOPTION_RESULT_LAYOUT;
}

function getSelectedEventAdoptionKinds (countableIds = []) {
    if (!countableIds.length) {
        return null;
    }

    const { featureIds, pageIds, trackEventIds } = splitCountableIdsByKind(countableIds);
    const selectedKinds = new Set();
    if (featureIds.length) selectedKinds.add('feature');
    if (pageIds.length) selectedKinds.add('page');
    if (trackEventIds.length) selectedKinds.add('trackEvent');

    return selectedKinds;
}

function getFilteredEventAdoptionLayout (segmentId, countableIds) {
    const layout = getEventAdoptionLayout(segmentId);
    const selectedKinds = getSelectedEventAdoptionKinds(countableIds);
    if (!selectedKinds) {
        return layout;
    }

    return layout.filter(({ kind }) => selectedKinds.has(kind));
}

export function buildAggregations (signals, context) {
    const aggregations = [];
    const breakpoints = [];

    signals.forEach((signal) => {
        const aggs = buildAggregationsForSignal(signal, context);
        aggregations.push(...aggs);
        breakpoints.push(aggregations.length);
    });

    return [aggregations, breakpoints];
}

function buildAggregationsForSignal (signal, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return buildVisitorAdoptionAggs(context);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return buildVisitorEngagementAggs(context);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return buildVisitorRageClicksAggs(context);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return buildEventAdoptionAggs(context);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return buildRetentionAggs(context, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return buildRetentionAggs(context, 'account');
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

export function parseAggregationResults (signals, breakpoints, rawData, context = {}) {
    const extractedData = {};
    let previousBreakpoint = 0;

    signals.forEach((signal, index) => {
        const relevantData = rawData.slice(previousBreakpoint, breakpoints[index]);
        previousBreakpoint = breakpoints[index];
        extractedData[signal] = parseAggregationResultsForSignal(signal, relevantData, context);
    });

    return extractedData;
}

function parseAggregationResultsForSignal (signal, relevantData, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return parseVisitorAppAdoptionResults(relevantData);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return parseVisitorAppEngagementResults(relevantData);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return parseVisitorAppRageClicksResults(relevantData);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return parseEventAdoptionResults(getFilteredEventAdoptionLayout(context.segmentId, context.countableIds), relevantData);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return parseRetentionRawForKind(relevantData, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return parseRetentionRawForKind(relevantData, 'account');
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

export function createSignals (signalsToFetch, extractedData, context) {
    const signals = [];

    signalsToFetch.forEach((signal) => {
        const dataPoints = extractedData[signal];
        const created = createSignal(signal, dataPoints, context);
        if (Array.isArray(created)) {
            signals.push(...created);
        } else if (created) {
            signals.push(created);
        }
    });

    return signals;
}

function createSignal (signal, dataPoints, context) {
    switch (signal) {
        case SIGNAL_TYPES.VISITOR_ADOPTION:
            return createVisitorAdoptionSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_ENGAGEMENT:
            return createVisitorEngagementSignal(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_RAGE_CLICKS:
            return createVisitorRageClicksSignal(dataPoints, context);
        case SIGNAL_TYPES.EVENT_ADOPTION:
            return createEventAdoptionSignals(dataPoints, context);
        case SIGNAL_TYPES.VISITOR_RETENTION:
            return createRetentionSignals(dataPoints, context, SIGNAL_TYPES.VISITOR_RETENTION, 'visitor');
        case SIGNAL_TYPES.ACCOUNT_RETENTION:
            return createRetentionSignals(dataPoints, context, SIGNAL_TYPES.ACCOUNT_RETENTION, 'account');
        default:
            throw new Error(`Unknown signal type: ${signal}`);
    }
}

// --- shared date helpers -----------------------------------------------------

function formatWeekLabel (start, end) {
    return start.isSame(end, 'month')
        ? `${start.format('MMMM D')}-${end.format('D')}`
        : `${start.format('MMMM D')}-${end.format('MMMM D')}`;
}

function formatDateRangeForSlack (start, end) {
    return `${start.format('MMMM D')} - ${end.format('MMMM D')}`;
}

function get7DayPeriodTimestamps () {
    function getTimestampForDayOffset (offset) { return moment(undefined, { ignoreTimeZone: true }).subtract(offset, 'days'); }

    return [getTimestampForDayOffset(1), getTimestampForDayOffset(7), getTimestampForDayOffset(8), getTimestampForDayOffset(14)];
}

function computeLast7DaysRange () {
    const end = moment(undefined, { ignoreTimeZone: true }).subtract(1, 'days');
    const start = end.clone().subtract(6, 'days');

    return {
        timeRangeStart: start.format('MMMM D'),
        timeRangeEnd: end.format('MMMM D'),
    };
}

function computeRetentionPeriodStart (timeZone) {
    const currentWeekStart = timeZone
        ? momentTimeZone.tz(timeZone).startOf('isoWeek')
        : moment(undefined, { ignoreTimeZone: true }).startOf('isoWeek');

    return currentWeekStart.clone().subtract(7, 'weeks').valueOf();
}

function computeRetentionAnalyticsRange (timeZone) {
    const currentWeekStart = timeZone
        ? momentTimeZone.tz(timeZone).startOf('isoWeek')
        : moment(undefined, { ignoreTimeZone: true }).startOf('isoWeek');

    return {
        timeRangeStart: currentWeekStart.clone().subtract(3, 'weeks').startOf('isoWeek').format('MMMM D'),
        timeRangeEnd: currentWeekStart.clone().subtract(2, 'weeks').endOf('isoWeek').format('MMMM D'),
    };
}

// --- countable helpers -------------------------------------------------------

function splitCountableIdsByKind (countableIds = []) {
    const featureIds = [];
    const pageIds = [];
    const trackEventIds = [];

    countableIds.forEach((countable) => {
        if (!countable?.id || !countable?.kind) {
            return;
        }
        if (countable.kind === 'Feature') {
            featureIds.push(countable.id);
        } else if (countable.kind === 'Page') {
            pageIds.push(countable.id);
        } else if (countable.kind === 'TrackType') {
            trackEventIds.push(countable.id);
        }
    });

    return { featureIds, pageIds, trackEventIds };
}

function buildAppIcon (rawIcon) {
    if (typeof rawIcon === 'string') {
        return { type: rawIcon, size: 12 };
    }
    if (rawIcon) {
        return { ...rawIcon, size: 12 };
    }

    return null;
}

function getCountableIcon (countableKind) {
    const kindMap = {
        feature: 'mouse-pointer',
        page: 'file',
        tracktype: 'zap',
    };
    const type = kindMap[countableKind?.toLowerCase()] || 'feature';

    return { type, size: 12 };
}

function getCountableTitle (countableKind) {
    const kindTitleMap = {
        feature: $t('trends.Feature adoption'),
        page: $t('trends.Page adoption'),
        tracktype: $t('trends.Track event adoption'),
    };

    return kindTitleMap[countableKind?.toLowerCase()] || $t('trends.Adoption');
}

// --- visitor adoption (single-app) ------------------------------------------

function buildVisitorAdoptionAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorAppAdoptionAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorAdoptionSignal (dataPoints, context = {}) {
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    return {
        type: SIGNAL_TYPES.VISITOR_ADOPTION,
        kind: 'visitor',
        title: $t('signals.Visitor_adoption'),

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousDataPoint === 0 && currentDataPoint > 0,
        previousDataPoint,
        currentDataPoint,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorAdoptionTextBefore'),
        textAfterTagName: buildVisitorAdoptionTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildVisitorAdoptionTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCount = addCommasToNumber(currentDataPoint);

    if ((previousDataPoint === 0 && currentDataPoint > 0) || delta === null) {
        return $t('signals.visitorAdoptionTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount,
        });
    }

    const previousCount = addCommasToNumber(previousDataPoint);

    if (delta === 0) {
        return $t('signals.visitorAdoptionTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount,
            previousCount,
        });
    }

    const key = delta > 0 ? 'signals.visitorAdoptionTextAfterIncrease' : 'signals.visitorAdoptionTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount,
        previousCount,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// --- visitor engagement -----------------------------------------------------

function buildVisitorEngagementAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorAppEngagementAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorEngagementSignal (dataPoints, context = {}) {
    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    const previousMinutes = previousDataPoint.minutes;
    const previousSeconds = previousDataPoint.seconds;
    const currentMinutes = currentDataPoint.minutes;
    const currentSeconds = currentDataPoint.seconds;

    return {
        type: SIGNAL_TYPES.VISITOR_ENGAGEMENT,
        kind: 'visitor',
        title: $t('signals.Visitor_engagement'),

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousMinutes + previousSeconds === 0 && currentMinutes + currentSeconds > 0,
        previousDataPoint: `${previousMinutes}m ${previousSeconds}s`,
        currentDataPoint: `${currentMinutes}m ${currentSeconds}s`,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorEngagementTextBefore'),
        textAfterTagName: buildVisitorEngagementTextAfter({ delta, currentPeriodLabel, previousMinutes, previousSeconds, currentMinutes, currentSeconds }),
    };
}

function buildVisitorEngagementTextAfter ({ delta, currentPeriodLabel, previousMinutes, previousSeconds, currentMinutes, currentSeconds }) {
    if ((previousMinutes + previousSeconds === 0 && currentMinutes + currentSeconds > 0) || delta === null) {
        return $t('signals.visitorEngagementTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentMinutes,
            currentSeconds,
        });
    }

    if (delta === 0) {
        return $t('signals.visitorEngagementTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentMinutes,
            currentSeconds,
            previousMinutes,
            previousSeconds,
        });
    }

    const key = delta > 0 ? 'signals.visitorEngagementTextAfterIncrease' : 'signals.visitorEngagementTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentMinutes,
        currentSeconds,
        previousMinutes,
        previousSeconds,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// functions specific to visitor rage clicks

function buildVisitorRageClicksAggs ({ appId, hasShowAnonymousFlag, segmentId }) {
    const { iterativeAggregations } = buildVisitorAppRageClicksAggregations({
        appId,
        hasShowAnonymousFlag,
        segmentId,
    });

    return iterativeAggregations.map(({ agg: { name, pipeline, productArea, subProductArea } }) => ({
        name,
        productArea,
        subProductArea,
        responseFormat: 'rows',
        build: () => pipeline,
    }));
}

function createVisitorRageClicksSignal (dataPoints, context = {}) {

    const [currentEnd, currentStart, previousEnd, previousStart] = get7DayPeriodTimestamps();
    const currentPeriodLabel = formatWeekLabel(currentStart, currentEnd);
    const previousPeriodLabel = formatWeekLabel(previousStart, previousEnd);
    const slackPeriodLabel = formatDateRangeForSlack(previousStart, currentEnd);

    const { appId, tagName, delta, previousDataPoint, currentDataPoint } = dataPoints;

    return {
        type: SIGNAL_TYPES.VISITOR_RAGE_CLICKS,
        kind: 'visitor',
        title: $t('signals.Visitor_frustration'),
        headlineLabel: $t('signals.rageClicksHeadlineLabel'),
        isNegativeMetric: true,

        appId,
        tagName,
        showAppTag: true,
        segmentId: context.segmentId,

        delta,
        previousPeriodHadNoData: previousDataPoint === 0 && currentDataPoint > 0,
        previousDataPoint,
        currentDataPoint,

        currentPeriodLabel,
        previousPeriodLabel,
        slackPeriodLabel,

        textBeforeTagName: $t('signals.visitorRageClicksTextBefore'),
        textAfterTagName: buildVisitorRageClicksTextAfter({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }),
    };
}

function buildVisitorRageClicksTextAfter ({ delta, currentPeriodLabel, previousDataPoint, currentDataPoint }) {
    const currentCount = addCommasToNumber(currentDataPoint);

    if ((previousDataPoint === 0 && currentDataPoint > 0) || delta === null) {
        return $t('signals.visitorRageClicksTextAfterNewBaseline', {
            currentPeriod: currentPeriodLabel,
            currentCount,
        });
    }

    const previousCount = addCommasToNumber(previousDataPoint);

    if (delta === 0) {
        return $t('signals.visitorRageClicksTextAfterUnchanged', {
            currentPeriod: currentPeriodLabel,
            currentCount,
            previousCount,
        });
    }

    const key = delta > 0 ? 'signals.visitorRageClicksTextAfterIncrease' : 'signals.visitorRageClicksTextAfterDecrease';

    return $t(key, {
        currentPeriod: currentPeriodLabel,
        currentCount,
        previousCount,
        delta: addCommasToNumber(Math.abs(delta)),
    });
}

// --- feature/page/track event adoption --------------------------------------------------

const EVENT_ADOPTION_METRIC_BUILDERS = {
    [EVENT_ADOPTION_METRICS.NEW_USERS]: buildEventAdoptionNewUsers,
    [EVENT_ADOPTION_METRICS.ALL_USERS_INCREASE]: buildEventAdoptionAllUsersIncrease,
    [EVENT_ADOPTION_METRICS.ALL_USERS_DECREASE]: buildEventAdoptionAllUsersDecrease,
};

function buildEventAdoptionAggs ({ appId, hasShowAnonymousFlag, countableIds = [], segmentId }) {
    const { featureIds, pageIds, trackEventIds } = splitCountableIdsByKind(countableIds);
    const options = {
        appId,
        hasShowAnonymousFlag,
        featureIds: featureIds.length ? featureIds : undefined,
        pageIds: pageIds.length ? pageIds : undefined,
        trackEventIds: trackEventIds.length ? trackEventIds : undefined,
        segmentId,
    };

    return getFilteredEventAdoptionLayout(segmentId, countableIds).map(({ metric, kind }) => {
        const { name, pipeline, productArea, subProductArea } = EVENT_ADOPTION_METRIC_BUILDERS[metric](options, kind);

        return {
            metric,
            kind,
            name: `${name}-${kind}`,
            productArea,
            subProductArea,
            responseFormat: 'rows',
            build: () => pipeline,
        };
    });
}

function createEventAdoptionSignals (parsedResults, context) {
    const { appData, countableIds = [] } = context;
    const hasSelectedEvents = countableIds.length > 0;
    const { timeRangeStart, timeRangeEnd } = computeLast7DaysRange();

    return parsedResults.map((parsedResult) => buildEventAdoptionSignal(parsedResult, appData, hasSelectedEvents, { timeRangeStart, timeRangeEnd }));
}

function buildEventAdoptionSignal (parsedResult, appData, hasSelectedEvents, timeRange) {
    const { metric, countableKind, countableId, countableName, adoptionRateCurrent, adoptionRatePrevious, delta, trendDirection } = parsedResult;
    const { textBeforeTagName, textAfterTagName } = buildEventAdoptionText(parsedResult, appData, hasSelectedEvents);
    const appIcon = buildAppIcon(appData?.icon);
    const tagIcon = getCountableIcon(countableKind);

    return {
        type: SIGNAL_TYPES.EVENT_ADOPTION,
        kind: countableKind,
        title: getCountableTitle(countableKind),
        textBeforeTagName,
        textAfterTagName,
        showAppTag: true,
        tagName: countableName,
        tagIcon,
        appName: appData?.name,
        appIcon,
        delta,
        trendDirection,
        adoptionRateCurrent,
        adoptionRatePrevious,
        countableId,
        countableKind,
        metric,
        appId: appData?.appId,
        timeRangeStart: timeRange.timeRangeStart,
        timeRangeEnd: timeRange.timeRangeEnd,
        slackPeriodLabel: `${timeRange.timeRangeStart} - ${timeRange.timeRangeEnd}`,
    };
}

function buildEventAdoptionText (parsedResult, appData, hasSelectedEvents) {
    const { metric, adoptionRateCurrent, adoptionRatePrevious, delta } = parsedResult;
    const currentRate = adoptionRateCurrent ?? 0;
    const previousRate = adoptionRatePrevious ?? 0;
    const absChange = delta != null ? Math.abs(delta) : 0;

    if (metric === EVENT_ADOPTION_METRICS.NEW_USERS) {
        const textBeforeTagName = hasSelectedEvents
            ? $t('signals.eventAdoptionNewUsersTextBeforeSelectedEvents')
            : $t('signals.eventAdoptionNewUsersTextBeforeApp', { appName: appData?.name ?? '' });

        return {
            textBeforeTagName,
            textAfterTagName: $t('signals.eventAdoptionNewUsersTextAfter', { currentRate }),
        };
    }

    if (metric === EVENT_ADOPTION_METRICS.ALL_USERS_INCREASE) {
        return {
            textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
            textAfterTagName: hasSelectedEvents
                ? $t('signals.eventAdoptionIncreaseTextAfterSelectedEvents', { absChange })
                : $t('signals.eventAdoptionIncreaseTextAfterApp', { absChange }),
        };
    }

    return {
        textBeforeTagName: $t('signals.eventAdoptionTextBefore'),
        textAfterTagName: hasSelectedEvents
            ? $t('signals.eventAdoptionDecreaseTextAfterSelectedEvents', { absChange, previousRate, currentRate })
            : $t('signals.eventAdoptionDecreaseTextAfterApp', { absChange, previousRate, currentRate }),
    };
}

// --- retention (visitor / account) ------------------------------------------

const RETENTION_PIPELINE_NAMES = ['newUsers', 'allUsers'];

function buildRetentionAggs ({ appId, timeZone, segmentId }, kind) {
    const startTimestamp = computeRetentionPeriodStart(timeZone);
    const builder = kind === 'account' ? buildAccountRetentionAggregations : buildVisitorRetentionAggregations;
    const { newUsers, allUsers } = builder({
        appId,
        hasShowAnonymousFlag: false,
        startTimestamp,
        segmentId,
    });

    return RETENTION_PIPELINE_NAMES.map((pipelineName) => {
        const pipelineDef = pipelineName === 'newUsers' ? newUsers : allUsers;

        return {
            name: pipelineDef.name,
            productArea: pipelineDef.productArea,
            subProductArea: pipelineDef.subProductArea,
            responseFormat: 'rows',
            build: () => pipelineDef.pipeline,
        };
    });
}

function parseRetentionRawForKind (relevantData, kind) {
    const [newUsersRows = [], allUsersRows = []] = relevantData;

    return { kind, newUsersRows, allUsersRows };
}

function createRetentionSignals (kindInput, context, signalType, kind) {
    const { appId, appData, timeZone, segmentId } = context;
    const parsed = parseRetentionTrendsResults({ appId, inputs: [kindInput] });
    const results = parsed?.data?.results ?? [];
    const { timeRangeStart, timeRangeEnd } = computeRetentionAnalyticsRange(timeZone);
    const tagName = appData?.name;
    const appIcon = buildAppIcon(appData?.icon);

    return results
        .filter((result) => result.metric === 'week_over_week')
        .map((result) => {
            const {
                current_retention: newUsersPercentage,
                previous_retention: existingUsersPercentage,
                delta,
                current_week_label: currentPeriodLabel,
                previous_week_label: previousPeriodLabel,
            } = result;
            const { textBefore, textAfter, showAppTag } = parseRetentionSummary(result.summary);
            const title = kind === 'account' ? $t('trends.Account retention') : $t('trends.Visitor retention');

            return {
                type: signalType,
                kind,
                title,
                newUsersPercentage,
                existingUsersPercentage,
                delta,
                currentPeriodLabel,
                previousPeriodLabel,
                textBeforeTagName: textBefore,
                textAfterTagName: textAfter,
                showAppTag,
                tagName,
                appIcon,
                appId: appData?.appId,
                segmentId,
                timeRangeStart,
                timeRangeEnd,
                slackPeriodLabel: `${timeRangeStart} - ${timeRangeEnd}`,
            };
        });
}

function parseRetentionSummary (summary) {
    if (!summary || typeof summary !== 'string') {
        return { textBefore: summary || '', textAfter: '', showAppTag: false };
    }

    const placeholderMatch = summary.match(/\{\{[^}]+\}\}/);

    if (placeholderMatch) {
        const textBefore = summary.substring(0, placeholderMatch.index).trim();
        const textAfter = summary.substring(placeholderMatch.index + placeholderMatch[0].length).trim();

        return { textBefore, textAfter, showAppTag: true };
    }

    return { textBefore: summary, textAfter: '', showAppTag: false };
}

// --- context helpers exposed for module use ---------------------------------

export function buildAppDataFromContext (rootGetters, appId) {
    const appDisplayName = rootGetters['trendsSettings/getAppDisplayName'];
    const trendsApp = rootGetters['trendsSettings/getApp'];
    const appIcon = rootGetters['apps/getIconByAppId']?.(appId);

    return {
        name: appDisplayName || trendsApp?.name,
        icon: appIcon,
        appId,
    };
}

export function getSubscriptionTimeZone (rootGetters) {
    const subscription = rootGetters['subscription/getActiveSub'];

    return subscription?.timeZone || null;
}
